/**
 * How a company name is DISPLAYED. The stored value is never touched.
 *
 * The ERP writes most names in caps — 5,933 of 7,868 are entirely uppercase, 1,907 are mixed —
 * so a list reads as ADVANCE MOVEMENT SERVICES next to Adia Thermal Ltd. This evens that out.
 *
 * ⚠️ Display only, and that is not a style choice. `company.name` is a JOIN KEY: the ERP's
 * contact and ledger tables carry no subject id, so both are matched to a company BY NAME. Writing
 * a normalised name back would silently detach a company's contacts and its payments. See
 * docs/etl/contacts.md — the project has already been bitten by renaming for display.
 *
 * Only fully-uppercase names are rewritten. Anything the ERP already wrote with lowercase in it
 * (`Adia Thermal Ltd`, `BUBLSHOP Ltd`) is left exactly as typed — someone chose that casing, and
 * guessing over it does more harm than the inconsistency.
 */

/** Legal forms, in the casing each language actually writes them. */
const LEGAL: Record<string, string> = {
  // SI / HR / RS — written lowercase by convention
  "D.O.O.": "d.o.o.", "D.D.": "d.d.", "S.P.": "s.p.", "D.N.O.": "d.n.o.", "K.D.": "k.d.",
  DOO: "d.o.o.", DD: "d.d.", SP: "s.p.",
  // Kept as acronyms
  SRL: "SRL", "S.R.L.": "S.R.L.", LLC: "LLC", KG: "KG", SAS: "SAS", SNC: "SNC", AG: "AG",
  AB: "AB", KFT: "KFT", SL: "SL", "S.L.": "S.L.", SA: "SA", "S.A.": "S.A.", BV: "BV",
  NV: "NV", AS: "AS", OY: "OY", PLC: "PLC", "S.R.O.": "S.R.O.", SRO: "S.R.O.", ZOO: "ZOO",
  // Mixed-case by convention
  GMBH: "GmbH", LTD: "Ltd", LTDA: "Ltda", INC: "Inc", CORP: "Corp", CO: "Co",
  SPA: "S.p.A.", "S.P.A.": "S.p.A.", MBH: "mbH",
};

/**
 * Connectors that stay lowercase inside a name — Slovenian, Italian, German, English.
 * Never applied to the first word: "In Trade" must not become "in Trade".
 */
const CONNECTORS = new Set(["IN", "ZA", "DI", "DE", "DA", "UND", "AND", "OF", "THE", "NA", "V", "S", "Z", "OD", "DEL", "DELLA", "LA", "LE", "EL"]);

const VOWELS = /[AEIOUYÁÉÍÓÚÀÈÌÒÙÄÖÜŠČŽ]/;

/** A short all-consonant token is an acronym, not a word: BPT, KSB, WH, BSS. */
function looksLikeAcronym(token: string): boolean {
  return token.length <= 4 && !VOWELS.test(token);
}

function titleCaseWord(word: string): string {
  // Hyphenated and slashed compounds get each part capitalised: BLACK-SEA → Black-Sea.
  return word
    .split(/([-/])/)
    .map((part) => (part === "-" || part === "/"
      ? part
      : part.charAt(0) + part.slice(1).toLocaleLowerCase()))
    .join("");
}

export function displayCompanyName(raw: string | null | undefined): string {
  if (!raw) return "";
  const name = raw.trim().replace(/\s+/g, " ");
  if (!name) return "";
  // Not all-caps → the ERP (or a person) chose this casing. Leave it.
  if (name !== name.toLocaleUpperCase()) return name;

  return name
    .split(" ")
    .map((token, i) => {
      const bare = token.replace(/[),.]+$/, "");
      const trail = token.slice(bare.length);
      const legal = LEGAL[token] ?? LEGAL[bare];
      if (legal) return (LEGAL[token] ? legal : legal + trail);
      if (looksLikeAcronym(bare)) return token;
      if (i > 0 && CONNECTORS.has(bare)) return token.toLocaleLowerCase();
      return titleCaseWord(token);
    })
    .join(" ");
}
